import React from "react";
import api from "../lib/api";

/**
 * Captura excepciones de render y muestra una pantalla de recuperación
 * en lugar de dejar la app en blanco. Sobre todo para flujos críticos
 * (Live Session, captura de pagos) donde un crash silencioso es peor
 * que un mensaje claro. Además reporta el error al backend para que
 * el admin reciba una notificación push y se persista en la DB.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Reporte al backend (best-effort, sin bloquear UI)
    try {
      api.post("/errors/report", {
        message: String(error?.message || error || "Error desconocido"),
        stack: String(error?.stack || "").slice(0, 4000),
        component_stack: String(info?.componentStack || "").slice(0, 4000),
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        context: this.props.context || null,
      }).catch(() => { /* offline-safe */ });
    } catch { /* noop */ }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") this.props.onReset();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="fixed inset-0 z-50 bg-app flex flex-col items-center justify-center p-6 text-center" data-testid="error-boundary">
        <div className="text-5xl mb-4">⚠️</div>
        <h2 className="font-display font-black uppercase text-2xl tracking-tighter mb-2">
          Algo no salió bien
        </h2>
        <p className="text-muted text-sm max-w-md mb-6">
          Ocurrió un error inesperado. Ya le avisamos al admin. Probá volver
          e iniciar la sesión nuevamente — tu progreso anterior está guardado.
        </p>
        <div className="flex gap-3">
          <button onClick={this.reset} className="btn-primary" data-testid="error-reset-btn">
            Reintentar
          </button>
          <button
            onClick={() => window.location.reload()}
            className="btn-secondary"
            data-testid="error-reload-btn"
          >
            Recargar app
          </button>
        </div>
      </div>
    );
  }
}
